import React from 'react';

import { Money, formatMoney, toMoney } from '@betterment/core';
import { Separator } from '@betterment/samba-react';
import { DrawerListRow } from '@betterment/shared-components/src/components/activity/components/drawer/common/DrawerListRow';
import { formatEnumLabel } from '@betterment/shared-components/src/utils/StringHelpers';

import { ActivityItemFragment } from '../../../../../graphql/fragments/activityItem.generated';
import { NarrowTypename } from '../../../../../graphql/utils/narrow-typename';
import { ActivityItemTrade } from '../../../types';
import { ActivityDrawerLayout } from '../common/ActivityDrawerLayout';
import { ActivityReportsUrl } from '../common/ActivityReportsUrl';
import { StatusSection } from '../common/StatusSection';
import { TradesList } from '../common/trades/TradesList';

const sumToMoney = (trades: { amount: Money }[]): Money => {
  return toMoney(trades.map(t => t.amount.cents).reduce((sum, cents) => sum + cents, 0));
};

export const Trade: React.FC<{
  item: NarrowTypename<ActivityItemFragment, 'ActivityItemTradeGroup'>;
}> = ({ item }) => {
  const unattributedPurchases: ActivityItemTrade[] = [];
  const unattributedSales: ActivityItemTrade[] = [];
  const groupedAttributedTrades: Record<string, ActivityItemTrade[]> = {};

  item.purchases.forEach(trade => {
    if (!trade.tradeAttribution) {
      unattributedPurchases.push(trade);
    } else {
      const key = `${formatEnumLabel(trade.tradeAttribution)} purchases`;
      (groupedAttributedTrades[key] ??= []).push(trade);
    }
  });

  item.sales.forEach(trade => {
    if (!trade.tradeAttribution) {
      unattributedSales.push(trade);
    } else {
      const key = `${formatEnumLabel(trade.tradeAttribution)} sales`;
      (groupedAttributedTrades[key] ??= []).push(trade);
    }
  });

  const { users } = item.subAccount;

  return (
    <ActivityDrawerLayout title="Trading Activity" users={users}>
      <DrawerListRow label="Account" value={item.subAccount?.name} />
      <Separator />
      {item.statuses.length > 0 && (
        <>
          <StatusSection statuses={item.statuses} />
          <Separator />
        </>
      )}

      {unattributedPurchases.length > 0 && (
        <>
          <TradesList
            title="Purchases"
            items={unattributedPurchases}
            subTitle={formatMoney(sumToMoney(unattributedPurchases))}
          />
          <Separator />
        </>
      )}

      {unattributedSales.length > 0 && (
        <>
          <TradesList title="Sales" items={unattributedSales} subTitle={formatMoney(sumToMoney(unattributedSales))} />
          <Separator />
        </>
      )}

      {Object.entries(groupedAttributedTrades).map(([attribution, trades]) => (
        <React.Fragment key={attribution}>
          <TradesList title={attribution} items={trades} subTitle={formatMoney(sumToMoney(trades))} />
          <Separator />
        </React.Fragment>
      ))}
      {item.activityReportsUrl && (
        <>
          <Separator />
          <ActivityReportsUrl url={item.activityReportsUrl} />
        </>
      )}
    </ActivityDrawerLayout>
  );
};
